import type { UseFormRegister, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { CATEGORY_OPTIONS } from "../category-badge";
import { Section } from "../view/section";
import { CurrencyInput, FormField, FormInput, FormSelect } from "./form-field";
import type { InsuranceFormData } from "./types";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

interface Props {
  register: UseFormRegister<InsuranceFormData>;
  setValue: UseFormSetValue<InsuranceFormData>;
  watch: UseFormWatch<InsuranceFormData>;
  propertyTypes: { id: string; name: string }[];
  subsoilOptions: { id: string; name: string }[];
  valuers: { id: string; name: string; qualifications: string }[];
}

export function SectionCertificateForm({
  register,
  setValue,
  watch,
  propertyTypes,
  subsoilOptions,
  valuers,
}: Props): React.ReactElement {
  const selectedValuerId = watch("valuerPersonId");
  const selectedValuer = valuers.find((v) => v.id === selectedValuerId);

  const copyYearBuilt = (): void => {
    const yb = watch("yearBuilt");
    if (yb != null) setValue("eyrBuilt", yb);
  };

  const setToday = (): void => {
    setValue("certificateDate", new Date().toISOString().split("T")[0] ?? "");
  };

  const feeAmountExGst = watch("feeAmountExGst");

  return (
    <Section id="certificate" title="Certificate Details">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          <FormField label="Type of Property" htmlFor="propertyTypeId" className="col-span-2">
            <FormSelect id="propertyTypeId" {...register("propertyTypeId")} data-testid="field-property-type">
              <option value="">Select...</option>
              {propertyTypes.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.name}
                </option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="Use / Occupation" htmlFor="useOcc" className="col-span-2">
            <FormInput id="useOcc" {...register("useOcc")} />
          </FormField>
          <FormField label="Property Category" htmlFor="propertyCategory" className="col-span-2">
            <FormSelect id="propertyCategory" {...register("propertyCategory")} data-testid="field-property-category">
              <option value="">Auto</option>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="Year Built" htmlFor="yearBuilt">
            <FormInput id="yearBuilt" type="number" {...register("yearBuilt", { valueAsNumber: true })} />
          </FormField>
          <FormField label="Effective Year Built" htmlFor="eyrBuilt">
            <FormInput id="eyrBuilt" type="number" {...register("eyrBuilt", { valueAsNumber: true })} />
            <button
              type="button"
              onClick={copyYearBuilt}
              className="text-xs text-primary hover:underline mt-0.5"
              data-testid="copy-year-built"
            >
              Copy from Year Built
            </button>
          </FormField>
          <FormField label="Land / Contour" htmlFor="land">
            <FormInput id="land" {...register("land")} />
          </FormField>
          <FormField label="Soil Type / Subsoil" htmlFor="subsoilId">
            <FormSelect id="subsoilId" {...register("subsoilId")}>
              <option value="">Select...</option>
              {subsoilOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="Upgrade Requirements" htmlFor="upgradeRequirements">
            <FormInput id="upgradeRequirements" {...register("upgradeRequirements")} />
          </FormField>
          <FormField label="Heritage Listing" htmlFor="heritageListing">
            <FormInput id="heritageListing" {...register("heritageListing")} />
          </FormField>
          <FormField label="Seismic Grade" htmlFor="seismicGrade">
            <FormInput id="seismicGrade" {...register("seismicGrade")} />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          <FormField label="Certificate From" htmlFor="pocFrom">
            <FormInput id="pocFrom" type="date" {...register("pocFrom")} />
          </FormField>
          <FormField label="Certificate To" htmlFor="pocTo">
            <FormInput id="pocTo" type="date" {...register("pocTo")} />
          </FormField>
          <FormField label="Date of Certificate Issue" htmlFor="certificateDate">
            <FormInput id="certificateDate" type="date" {...register("certificateDate")} />
            <button
              type="button"
              onClick={setToday}
              className="text-xs text-primary hover:underline mt-0.5"
              data-testid="set-today"
            >
              Set to today
            </button>
          </FormField>
          <FormField label="Update Month" htmlFor="updateMonth">
            <FormSelect id="updateMonth" {...register("updateMonth")}>
              <option value="">Select...</option>
              {MONTHS.map((m, i) => (
                <option key={m} value={String(i + 1)}>
                  {m}
                </option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="Valuer" htmlFor="valuerPersonId" className="col-span-2">
            <FormSelect id="valuerPersonId" {...register("valuerPersonId")} data-testid="field-valuer">
              <option value="">Select valuer...</option>
              {valuers.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </FormSelect>
            {selectedValuer && (
              <div className="mt-1 text-xs text-muted-foreground">
                <span className="font-medium">{selectedValuer.name}</span> — {selectedValuer.qualifications}
              </div>
            )}
          </FormField>
          <FormField label="Fee Amount (ex GST)" htmlFor="feeAmountExGst">
            <CurrencyInput
              id="feeAmountExGst"
              value={feeAmountExGst}
              onChange={(v) => setValue("feeAmountExGst", v, { shouldDirty: true })}
            />
          </FormField>
          <FormField label="Job Number for Xero" htmlFor="accountNumberForXero">
            <FormInput id="accountNumberForXero" {...register("accountNumberForXero")} />
          </FormField>
        </div>
      </div>
    </Section>
  );
}
